import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiStar, FiSend, FiCheck } from 'react-icons/fi';
import api from '@/services/api';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const StarRating = ({ rating, setRating, label }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-400">{label}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <FiStar
              size={32}
              className={`${
                star <= (hover || rating)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-600'
              } transition-colors`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const ReviewOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [order, setOrder] = useState(null);
  const [storeRating, setStoreRating] = useState(0);
  const [driverRating, setDriverRating] = useState(0);
  const [storeComment, setStoreComment] = useState('');
  const [driverComment, setDriverComment] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
    } catch (err) {
      setError('Pedido não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (storeRating === 0) {
      setError('Por favor, avalie a loja');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      await api.post('/reviews', {
        order_id: orderId,
        store_rating: storeRating,
        driver_rating: driverRating || null,
        store_comment: storeComment || null,
        driver_comment: driverComment || null
      });
      
      setSubmitted(true);
      setTimeout(() => navigate('/cliente/pedidos'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCheck size={40} />
          </div>
          <h2 className="text-2xl font-bold mb-2">Obrigado!</h2>
          <p className="text-gray-400">Sua avaliação foi enviada com sucesso.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A1A1A] to-[#DC143C] border-b border-gray-700">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-full">
              <FiArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold">Avaliar Pedido</h1>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {order && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Store Review */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h2 className="font-semibold text-lg mb-4">Avalie a Loja</h2>
              <p className="text-gray-400 mb-4">{order.store_name}</p>
              
              <StarRating 
                rating={storeRating} 
                setRating={setStoreRating} 
                label="Qualidade do produto e atendimento"
              />
              
              <textarea
                value={storeComment}
                onChange={(e) => setStoreComment(e.target.value)}
                placeholder="Deixe um comentário sobre a loja (opcional)"
                className="w-full mt-4 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 resize-none"
                rows={3}
              />
            </div>

            {/* Driver Review */}
            {order.delivery_person_id && (
              <div className="bg-gray-800 rounded-xl p-6">
                <h2 className="font-semibold text-lg mb-4">Avalie o Entregador</h2>
                <p className="text-gray-400 mb-4">{order.delivery_person_name || 'Entregador'}</p>
                
                <StarRating 
                  rating={driverRating} 
                  setRating={setDriverRating} 
                  label="Pontualidade e cordialidade"
                />
                
                <textarea
                  value={driverComment}
                  onChange={(e) => setDriverComment(e.target.value)}
                  placeholder="Deixe um comentário sobre o entregador (opcional)"
                  className="w-full mt-4 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 resize-none"
                  rows={3}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || storeRating === 0}
              className="w-full py-4 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <LoadingSpinner size="sm" /> Enviando...
                </>
              ) : (
                <>
                  <FiSend /> Enviar Avaliação
                </>
              )}
            </button>
          </form>
        )}
      </main>
    </div>
  );
};

export default ReviewOrder;
